import {
  OnInit,
  Component,
  Inject,
  inject,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SharedModule } from '../../shared/shared.module';
import { ModelService, PluginInjectorService, PluginInjectorWidget } from 'core-client/api';
import { MatSelectChange } from '@angular/material/select';
import { VideoStreamService } from '../../shared/services/video-stream.service';
import { IAhoyrtcVideoStreamWidget, ICameraList } from '../../shared/interfaces/ahyortc-video-stream-widget.interface';

type IWidgetHeaderOptions = 'alwaysDisplayed' | 'onHoverDisplayed' | 'alwaysOff' | 'transparent'

@Component({
    selector: 'lib-ahoyrtc-video-widget-settings',
    imports: [
        SharedModule
    ],
    templateUrl: './ahoyrtc-video-widget-settings.component.html',
    styleUrl: './ahoyrtc-video-widget-settings.component.scss'
})
export class AhoyrtcVideoWidgetSettingsComponent implements OnInit {
  private readonly videoStreamService: VideoStreamService = inject(VideoStreamService);
  protected readonly pluginInjectorService = inject(PluginInjectorService);
  protected readonly modelService = inject(ModelService);
  protected readonly translate = inject(TranslateService);
  protected readonly dialogRef: MatDialogRef<AhoyrtcVideoWidgetSettingsComponent, any> =
      inject(MatDialogRef<AhoyrtcVideoWidgetSettingsComponent>);

  protected ahyortcVideoStreamWidget: IAhoyrtcVideoStreamWidget;
  selectedWidgetHeaderOption: string = 'alwaysDisplayed';
  widgetTitle: string;
  showWidgetTitle: boolean = false;
  widgetId: string;
  streamUuid: string | undefined;
  streams: any[] = []
  cameras: ICameraList[] = [];

  constructor(@Inject(MAT_DIALOG_DATA) protected readonly data: PluginInjectorWidget.IWidget) {
    this.ahyortcVideoStreamWidget = data.appPluginWidget;
    this.widgetId = data.appPluginWidget._id;
    this.widgetTitle = data.appPluginWidget.name;
    this.showWidgetTitle = data.appPluginWidget.settings.showWidgetTitle;
    const widgetHeaderOption = data.appPluginWidget.settings.widgetHeader;
    const widgetHeaderOptions = Object.keys(widgetHeaderOption) as IWidgetHeaderOptions[];
    this.selectedWidgetHeaderOption = widgetHeaderOptions.filter(
        (key: IWidgetHeaderOptions) => widgetHeaderOption[key]
    )[0];

    this.streamUuid = this.ahyortcVideoStreamWidget.settings?.streamUuid;
  }

  async ngOnInit(): Promise<void> {
    const requestHeader = this.videoStreamService.getRequestHeader();

    if (requestHeader) {
      const apiKey = requestHeader.get('x-api-key');
      const apiUrl = requestHeader.get('x-proxy-target');

      if (apiKey && apiUrl) {
        this.cameras = await this.videoStreamService.availableCameras();
      }
    }
  }

  onStreamChange(uuid: string): void {
    this.streamUuid = uuid;
  }

  async onSubmit(): Promise<void> {
    if (!this.ahyortcVideoStreamWidget) {
      return;
    }

    this.ahyortcVideoStreamWidget.name = this.widgetTitle;
    this.ahyortcVideoStreamWidget.settings.streamUuid = this.streamUuid;
    await this.modelService._appPluginWidget.upsertOne(this.ahyortcVideoStreamWidget);

    const widget = this.pluginInjectorService.widgetService.getWidgetItem(this.ahyortcVideoStreamWidget._id);
    if (widget) {
      widget.name = this.ahyortcVideoStreamWidget.name; // data-binding changes
    }

    this.videoStreamService.setStreamUuid(this.widgetId, this.streamUuid);

    this.dialogRef.close();
  }

  selectHeaderOption($event: MatSelectChange): void {
    const headerOptions: Record<string, { alwaysDisplayed: boolean; onHoverDisplayed: boolean; alwaysOff: boolean; transparent: boolean }> = {
      alwaysDisplayed: { alwaysDisplayed: true, onHoverDisplayed: false, alwaysOff: false, transparent: false },
      onHoverDisplayed: { alwaysDisplayed: false, onHoverDisplayed: true, alwaysOff: false, transparent: false },
      alwaysOff: { alwaysDisplayed: false, onHoverDisplayed: false, alwaysOff: true, transparent: false },
      transparent: { alwaysDisplayed: false, onHoverDisplayed: false, alwaysOff: false, transparent: true }
    };

    if ($event.value in headerOptions) {
      this.ahyortcVideoStreamWidget.settings.widgetHeader = headerOptions[$event.value];
    }
  }

  getText(key: unknown): string {
    const translations: Record<string, string> = {
      alwaysDisplayed: 'STREAMING.AHOY_VIDEO.WIDGET.WIDGET_HEADER.ALWAYS_DISPLAYED',
      onHoverDisplayed: 'STREAMING.AHOY_VIDEO.WIDGET.WIDGET_HEADER.ON_HOVER_DISPLAYED',
      alwaysOff: 'STREAMING.AHOY_VIDEO.WIDGET.WIDGET_HEADER.ALWAYS_OFF',
      transparent: 'STREAMING.AHOY_VIDEO.WIDGET.WIDGET_HEADER.TRANSPARENT'
    };

    return this.translate.instant(translations[key as string] || 'unknown');
  }
}
